import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { GameData, EndGameData, TournamentOverData, TournamentOverDrawData, NextRoundData, ByeMessage, CancelledTournament } from '@/shared/types';

/**
 * GOLDEN STANDARD: Event-Driven State Machine
 *
 * This is the SINGLE SOURCE OF TRUTH for game session state.
 * - State transitions happen ONLY through reducers (not components)
 * - Events trigger actions, actions update state
 * - Components are pure presentation (no business logic)
 * - No manual cleanup needed (transitions auto-cleanup)
 *
 * NOTE: User identity (currentUserId) is stored in authSlice, not here.
 * This slice manages session state, not user identity.
 */

export enum SessionState {
  LOBBY = 'LOBBY',
  WAITING_FOR_ROUND = 'WAITING_FOR_ROUND',
  BYE_ROUND = 'BYE_ROUND',
  IN_GAME = 'IN_GAME',
  GAME_OVER = 'GAME_OVER',
  TOURNAMENT_OVER = 'TOURNAMENT_OVER',
  TOURNAMENT_CANCELLED = 'TOURNAMENT_CANCELLED'
}

interface GameSessionState {
  // State machine
  currentState: SessionState;

  // Current game data (null when no active game)
  game: GameData | null;

  // Tournament context
  tournamentId: number | null;

  // Terminal state data
  gameOverData: EndGameData | null;
  tournamentOverData: TournamentOverData | null;
  tournamentOverDrawData: TournamentOverDrawData | null;

  // Transient messages (cleared after display)
  byeMessage: ByeMessage | null;
  nextRoundMessage: NextRoundData | null;
  cancelledTournament: CancelledTournament | null;

  // Metadata
  lastTransitionAt: number;
  transitionHistory: Array<{ from: SessionState; to: SessionState; at: number }>;
}

const initialState: GameSessionState = {
  currentState: SessionState.LOBBY,
  game: null,
  tournamentId: null,
  gameOverData: null,
  tournamentOverData: null,
  tournamentOverDrawData: null,
  byeMessage: null,
  nextRoundMessage: null,
  cancelledTournament: null,
  lastTransitionAt: Date.now(),
  transitionHistory: []
};

// Helper: Validate and execute state transition
function transition(
  state: GameSessionState,
  targetState: SessionState,
  logMessage?: string
): boolean {
  if (state.currentState === targetState) {
    return true;
  }

  const validTransitions: Record<SessionState, SessionState[]> = {
    [SessionState.LOBBY]: [SessionState.WAITING_FOR_ROUND, SessionState.TOURNAMENT_CANCELLED],
    // Backend game_start is authoritative and may arrive without an intermediate "game starting" phase.
    [SessionState.WAITING_FOR_ROUND]: [SessionState.IN_GAME, SessionState.BYE_ROUND, SessionState.TOURNAMENT_OVER, SessionState.TOURNAMENT_CANCELLED],
    [SessionState.BYE_ROUND]: [SessionState.WAITING_FOR_ROUND, SessionState.IN_GAME, SessionState.TOURNAMENT_OVER, SessionState.TOURNAMENT_CANCELLED],
    [SessionState.IN_GAME]: [SessionState.GAME_OVER, SessionState.TOURNAMENT_CANCELLED],
    // Be tolerant to missed "next_round" when reconnecting; a fresh game_start can move directly to IN_GAME.
    [SessionState.GAME_OVER]: [SessionState.WAITING_FOR_ROUND, SessionState.IN_GAME, SessionState.TOURNAMENT_OVER, SessionState.TOURNAMENT_CANCELLED],
    [SessionState.TOURNAMENT_OVER]: [],
    [SessionState.TOURNAMENT_CANCELLED]: []
  };

  const allowed = validTransitions[state.currentState].includes(targetState);

  if (!allowed) {
    console.error(
      `❌ Invalid transition: ${state.currentState} → ${targetState}\n` +
      `Valid transitions: ${validTransitions[state.currentState].join(', ')}`
    );
    return false;
  }

  // Record transition
  state.transitionHistory.push({
    from: state.currentState,
    to: targetState,
    at: Date.now()
  });

  // Keep last 20 transitions
  if (state.transitionHistory.length > 20) {
    state.transitionHistory.shift();
  }

  state.currentState = targetState;
  state.lastTransitionAt = Date.now();

  return true;
}

const gameSessionSlice = createSlice({
  name: 'gameSession',
  initialState,
  reducers: {
    // ==========================================
    // TOURNAMENT LIFECYCLE
    // ==========================================

    tournamentJoined: (state, action: PayloadAction<number>) => {
      // Joining a tournament should always start from a clean waiting state.
      // This prevents stale previous-game UI from persisting across joins.
      state.tournamentId = action.payload;
      state.game = null;
      state.gameOverData = null;
      state.tournamentOverData = null;
      state.tournamentOverDrawData = null;
      state.byeMessage = null;
      state.nextRoundMessage = null;
      state.cancelledTournament = null;

      if (state.currentState !== SessionState.WAITING_FOR_ROUND) {
        state.transitionHistory.push({
          from: state.currentState,
          to: SessionState.WAITING_FOR_ROUND,
          at: Date.now()
        });
        if (state.transitionHistory.length > 20) {
          state.transitionHistory.shift();
        }
        state.currentState = SessionState.WAITING_FOR_ROUND;
        state.lastTransitionAt = Date.now();
      }
    },

    tournamentCancelled: (state, action: PayloadAction<CancelledTournament>) => {
      if (action.payload.tournamentID === state.tournamentId) {
        state.cancelledTournament = action.payload;
        transition(state, SessionState.TOURNAMENT_CANCELLED, 'tournament cancelled');
      }
    },

    tournamentOver: (state, action: PayloadAction<TournamentOverData>) => {
      if (action.payload.tournamentId === state.tournamentId) {
        state.tournamentOverData = action.payload;

        // Can only transition from GAME_OVER or WAITING_FOR_ROUND
        if (state.currentState === SessionState.GAME_OVER ||
            state.currentState === SessionState.WAITING_FOR_ROUND ||
            state.currentState === SessionState.BYE_ROUND) {
          transition(state, SessionState.TOURNAMENT_OVER, 'tournament completed');
        }
      }
    },

    tournamentOverDraw: (state, action: PayloadAction<TournamentOverDrawData>) => {
      if (action.payload.tournamentId === state.tournamentId) {
        state.tournamentOverDrawData = action.payload;

        if (state.currentState === SessionState.GAME_OVER ||
            state.currentState === SessionState.WAITING_FOR_ROUND ||
            state.currentState === SessionState.BYE_ROUND) {
          transition(state, SessionState.TOURNAMENT_OVER, 'tournament ended in draw');
        }
      }
    },

    // ==========================================
    // ROUND LIFECYCLE
    // ==========================================

    nextRoundStarting: (state, action: PayloadAction<NextRoundData>) => {
      if (action.payload.tournamentId === state.tournamentId) {
        state.nextRoundMessage = action.payload;

        // Clear previous game data
        state.gameOverData = null;
        state.game = null;

        // Transition to waiting from any active round state.
        if (
          state.currentState === SessionState.GAME_OVER ||
          state.currentState === SessionState.IN_GAME ||
          state.currentState === SessionState.BYE_ROUND
        ) {
          transition(state, SessionState.WAITING_FOR_ROUND, 'next round starting');
        }
      }
    },

    byeRoundReceived: (state, action: PayloadAction<ByeMessage>) => {
      if (action.payload.tournamentId === state.tournamentId) {
        state.byeMessage = action.payload;

        if (state.currentState === SessionState.WAITING_FOR_ROUND) {
          transition(state, SessionState.BYE_ROUND, 'bye round');
        }
      }
    },

    // ==========================================
    // GAME LIFECYCLE
    // ==========================================

    gameStarting: (state, action: PayloadAction<GameData>) => {
      // NOTE: User validation (whiteId/blackId check) is now done in websocketMiddleware
      // using authSlice. This reducer trusts that middleware has already validated.

      // Safety: ignore game_start from a different tournament than the active session.
      // This prevents stale events from affecting the current session.
      if (state.tournamentId !== null && action.payload.tournamentId !== state.tournamentId) {
        return;
      }

      state.tournamentId = action.payload.tournamentId;
      state.game = action.payload;

      // Clear any previous game-over data
      state.gameOverData = null;
      state.nextRoundMessage = null;
      state.byeMessage = null;

      // If coming from LOBBY, first transition to WAITING_FOR_ROUND
      if (state.currentState === SessionState.LOBBY) {
        transition(state, SessionState.WAITING_FOR_ROUND, 'tournament joined implicitly');
      }

      // Transition directly into IN_GAME on authoritative game_start event.
      if (state.currentState === SessionState.WAITING_FOR_ROUND ||
          state.currentState === SessionState.BYE_ROUND ||
          state.currentState === SessionState.GAME_OVER) {
        transition(state, SessionState.IN_GAME, `game ${action.payload.gameId} started`);
      } else if (state.currentState === SessionState.IN_GAME) {
        // Idempotent duplicate game_start delivery.
        transition(state, SessionState.IN_GAME, `game ${action.payload.gameId} start replay`);
      }
    },

    gameEnded: (state, action: PayloadAction<EndGameData>) => {
      // Only process if it's the current game
      if (state.game && action.payload.gameId === state.game.gameId) {
        state.gameOverData = action.payload;

        if (state.currentState === SessionState.IN_GAME) {
          transition(state, SessionState.GAME_OVER, `game ${action.payload.gameId} ended`);
        }
      }
    },

    // ==========================================
    // TRANSIENT MESSAGE CLEANUP
    // ==========================================

    clearByeMessage: (state) => {
      state.byeMessage = null;
    },

    clearNextRoundMessage: (state) => {
      state.nextRoundMessage = null;
    },

    clearCancelledTournament: (state) => {
      state.cancelledTournament = null;
    },

    clearGameOverData: (state) => {
      state.gameOverData = null;
    },

    clearTournamentOverData: (state) => {
      state.tournamentOverData = null;
    },

    clearTournamentOverDrawData: (state) => {
      state.tournamentOverDrawData = null;
    },

    // ==========================================
    // SESSION RESET
    // ==========================================

    resetSession: () => initialState
  }
});

export const {
  tournamentJoined,
  tournamentCancelled,
  tournamentOver,
  tournamentOverDraw,
  nextRoundStarting,
  byeRoundReceived,
  gameStarting,
  gameEnded,
  clearByeMessage,
  clearNextRoundMessage,
  clearCancelledTournament,
  clearGameOverData,
  clearTournamentOverData,
  clearTournamentOverDrawData,
  resetSession
} = gameSessionSlice.actions;

// ==========================================
// SELECTORS
// ==========================================

export const selectSessionState = (state: RootState) => state.gameSession.currentState;
export const selectCurrentGame = (state: RootState) => state.gameSession.game;
export const selectTournamentId = (state: RootState) => state.gameSession.tournamentId;
export const selectGameOverData = (state: RootState) => state.gameSession.gameOverData;
export const selectTournamentOverData = (state: RootState) => state.gameSession.tournamentOverData;
export const selectTournamentOverDrawData = (state: RootState) => state.gameSession.tournamentOverDrawData;
export const selectByeMessage = (state: RootState) => state.gameSession.byeMessage;
export const selectNextRoundMessage = (state: RootState) => state.gameSession.nextRoundMessage;
export const selectCancelledTournament = (state: RootState) => state.gameSession.cancelledTournament;

// Derived selectors (computed state)
export const selectIsInLobby = (state: RootState) => state.gameSession.currentState === SessionState.LOBBY;
export const selectIsWaitingForRound = (state: RootState) => state.gameSession.currentState === SessionState.WAITING_FOR_ROUND;
export const selectIsByeRound = (state: RootState) => state.gameSession.currentState === SessionState.BYE_ROUND;
export const selectIsInGame = (state: RootState) => state.gameSession.currentState === SessionState.IN_GAME;
export const selectIsGameOver = (state: RootState) => state.gameSession.currentState === SessionState.GAME_OVER;
export const selectIsTournamentOver = (state: RootState) => state.gameSession.currentState === SessionState.TOURNAMENT_OVER;
export const selectIsTournamentCancelled = (state: RootState) => state.gameSession.currentState === SessionState.TOURNAMENT_CANCELLED;

// Can play game if in IN_GAME state
export const selectCanPlayGame = (state: RootState) => state.gameSession.currentState === SessionState.IN_GAME;

// Should show game board (IN_GAME or GAME_OVER with game data)
// This encapsulates the business logic of "when to render the game board"
export const selectShouldShowGameBoard = (state: RootState) => {
  const currentState = state.gameSession.currentState;
  const hasGameData = state.gameSession.game !== null;
  return (currentState === SessionState.IN_GAME || currentState === SessionState.GAME_OVER) && hasGameData;
};

// Debug selector
export const selectTransitionHistory = (state: RootState) => state.gameSession.transitionHistory;

export default gameSessionSlice.reducer;
